import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ffeAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Search, Loader2, ChevronRight, ChevronLeft, FolderOpen, DoorOpen,
  Store, Package, Sofa, GripVertical,
} from 'lucide-react';

const STAGES = [
  { key: 'sourcing', label: 'Sourcing', color: 'border-t-gray-400', bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-700' },
  { key: 'quoted', label: 'Quoted', color: 'border-t-blue-400', bg: 'bg-blue-50/30', badge: 'bg-blue-100 text-blue-700' },
  { key: 'approved', label: 'Approved', color: 'border-t-green-400', bg: 'bg-green-50/30', badge: 'bg-green-100 text-green-700' },
  { key: 'ordered', label: 'Ordered', color: 'border-t-purple-400', bg: 'bg-purple-50/30', badge: 'bg-purple-100 text-purple-700' },
  { key: 'shipped', label: 'Shipped', color: 'border-t-amber-400', bg: 'bg-amber-50/30', badge: 'bg-amber-100 text-amber-700' },
  { key: 'received', label: 'Received', color: 'border-t-teal-400', bg: 'bg-teal-50/30', badge: 'bg-teal-100 text-teal-700' },
  { key: 'installed', label: 'Installed', color: 'border-t-emerald-500', bg: 'bg-emerald-50/30', badge: 'bg-emerald-100 text-emerald-800' },
];

const fmt = (n) => typeof n === 'number' && n > 0 ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '';

export default function ProcurementPipeline() {
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [moveDialog, setMoveDialog] = useState(null);
  const [updating, setUpdating] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (projectFilter !== 'all') params.project_id = projectFilter;
      if (searchTerm) params.search = searchTerm;
      const [itemsRes, summaryRes] = await Promise.all([
        ffeAPI.list(params),
        ffeAPI.summary(),
      ]);
      setItems(itemsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to load pipeline:', err);
    } finally {
      setLoading(false);
    }
  }, [projectFilter, searchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const moveItem = async (ffeId, newStatus) => {
    setUpdating(ffeId);
    try {
      await ffeAPI.update(ffeId, { status: newStatus });
      setItems(prev => prev.map(i => i.ffe_id === ffeId ? { ...i, status: newStatus } : i));
      setMoveDialog(null);
    } catch (err) {
      console.error('Failed to move item:', err);
    } finally {
      setUpdating(null);
    }
  };

  const moveToNext = async (item) => {
    const idx = STAGES.findIndex(s => s.key === item.status);
    if (idx < STAGES.length - 1) {
      await moveItem(item.ffe_id, STAGES[idx + 1].key);
    }
  };

  const moveToPrev = async (item) => {
    const idx = STAGES.findIndex(s => s.key === item.status);
    if (idx > 0) {
      await moveItem(item.ffe_id, STAGES[idx - 1].key);
    }
  };

  // Group items by status
  const grouped = {};
  for (const s of STAGES) {
    grouped[s.key] = items.filter(i => i.status === s.key);
  }

  const filterProjects = summary?.filters?.projects || [];
  const totalItems = items.length;
  const totalValue = items.reduce((s, i) => s + (i.client_price || 0) * (i.quantity || 1), 0);

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="procurement-pipeline-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight flex items-center gap-3">
              <Package className="h-7 w-7 text-muted-foreground" />
              Procurement Pipeline
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Track FF&E items from sourcing to installation — {totalItems} items, {fmt(totalValue)} total value
            </p>
          </div>
          <Link to="/ffe-library">
            <Button variant="outline" data-testid="go-to-ffe-btn">
              <Sofa className="h-4 w-4 mr-2" />FF&E Library
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search items..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="pipeline-search" />
              </div>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="w-full sm:w-52" data-testid="pipeline-project-filter">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {filterProjects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Stage Summary Bar */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          {STAGES.map(stage => {
            const count = grouped[stage.key]?.length || 0;
            const stageValue = (grouped[stage.key] || []).reduce((s, i) => s + (i.client_price || 0) * (i.quantity || 1), 0);
            return (
              <div key={stage.key} className={`flex-1 min-w-[100px] p-2 text-center border-t-2 ${stage.color} rounded-sm bg-card`} data-testid={`stage-summary-${stage.key}`}>
                <p className="text-lg font-light">{count}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{stage.label}</p>
                {stageValue > 0 && <p className="text-[10px] text-muted-foreground">{fmt(stageValue)}</p>}
              </div>
            );
          })}
        </div>

        {/* Kanban Board */}
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '400px' }}>
          {STAGES.map(stage => {
            const stageItems = grouped[stage.key] || [];
            return (
              <div
                key={stage.key}
                className={`flex-shrink-0 w-64 flex flex-col rounded border-t-2 ${stage.color} ${stage.bg}`}
                data-testid={`pipeline-column-${stage.key}`}
              >
                {/* Column Header */}
                <div className="p-3 border-b border-border/50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">{stage.label}</h3>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${stage.badge}`}>
                      {stageItems.length}
                    </span>
                  </div>
                </div>

                {/* Cards */}
                <div className="flex-1 p-2 space-y-2 overflow-y-auto" style={{ maxHeight: '600px' }}>
                  {stageItems.length === 0 ? (
                    <div className="py-8 text-center text-xs text-muted-foreground/50">
                      No items
                    </div>
                  ) : (
                    stageItems.map(item => {
                      const stageIdx = STAGES.findIndex(s => s.key === stage.key);
                      const isUpdating = updating === item.ffe_id;
                      return (
                        <div
                          key={item.ffe_id}
                          className="bg-card border rounded p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                          onClick={() => canEdit && setMoveDialog(item)}
                          data-testid={`pipeline-card-${item.ffe_id}`}
                        >
                          {/* Item Name */}
                          <p className="text-sm font-medium truncate">{item.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="capitalize text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{item.category}</span>
                            {item.quantity > 1 && <span className="text-[10px] text-muted-foreground">x{item.quantity}</span>}
                          </div>

                          {/* Meta */}
                          <div className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                            {item.project_name && (
                              <p className="flex items-center gap-1 truncate"><FolderOpen className="h-3 w-3 shrink-0" />{item.project_name}</p>
                            )}
                            {item.room_name && (
                              <p className="flex items-center gap-1 truncate"><DoorOpen className="h-3 w-3 shrink-0" />{item.room_name}</p>
                            )}
                            {item.vendor_name && (
                              <p className="flex items-center gap-1 truncate"><Store className="h-3 w-3 shrink-0" />{item.vendor_name}</p>
                            )}
                          </div>

                          {/* Price */}
                          {item.client_price > 0 && (
                            <p className="text-sm font-medium mt-2">{fmt(item.client_price * (item.quantity || 1))}</p>
                          )}

                          {/* Quick Move Buttons */}
                          {canEdit && (
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30 opacity-0 group-hover:opacity-100 transition-opacity">
                              {stageIdx > 0 ? (
                                <Button
                                  variant="ghost" size="sm" className="h-6 text-[10px] px-2"
                                  onClick={(e) => { e.stopPropagation(); moveToPrev(item); }}
                                  disabled={isUpdating}
                                  data-testid={`move-prev-${item.ffe_id}`}
                                >
                                  <ChevronLeft className="h-3 w-3 mr-0.5" />{STAGES[stageIdx - 1].label}
                                </Button>
                              ) : <span />}
                              {stageIdx < STAGES.length - 1 ? (
                                <Button
                                  variant="ghost" size="sm" className="h-6 text-[10px] px-2"
                                  onClick={(e) => { e.stopPropagation(); moveToNext(item); }}
                                  disabled={isUpdating}
                                  data-testid={`move-next-${item.ffe_id}`}
                                >
                                  {STAGES[stageIdx + 1].label}<ChevronRight className="h-3 w-3 ml-0.5" />
                                </Button>
                              ) : <span />}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Move Dialog — click card to jump to any stage */}
        <Dialog open={!!moveDialog} onOpenChange={() => setMoveDialog(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Move Item</DialogTitle>
              <DialogDescription>Move "{moveDialog?.name}" to a different stage</DialogDescription>
            </DialogHeader>
            {moveDialog && (
              <div className="space-y-2 py-2">
                {STAGES.map(stage => {
                  const isCurrent = stage.key === moveDialog.status;
                  return (
                    <button
                      key={stage.key}
                      className={`w-full flex items-center justify-between p-3 rounded border transition-all ${isCurrent ? 'border-primary bg-primary/5 font-medium' : 'border-border hover:bg-muted/50'}`}
                      onClick={() => !isCurrent && moveItem(moveDialog.ffe_id, stage.key)}
                      disabled={isCurrent || !!updating}
                      data-testid={`move-to-${stage.key}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${stage.color.replace('border-t-', 'bg-')}`} />
                        <span className="text-sm">{stage.label}</span>
                      </div>
                      {isCurrent && <span className="text-[10px] text-primary">Current</span>}
                      {updating === moveDialog.ffe_id && !isCurrent && <Loader2 className="h-3 w-3 animate-spin" />}
                    </button>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
